import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useMaintenanceContractDetail, useMaintenanceContracts, useContractScopes, useContractSLA, useContractZones, useRemoveContractZone } from './hooks/useMaintenanceContracts';
import { Button, Badge, Card, FeaturePlaceholder } from '@/components/ui';
import { ContractFormModal } from './components/ContractFormModal';
import { ContractZonesTable } from './components/ContractZonesTable';
import { ContractZonesMobileList } from './components/ContractZonesMobileList';
import { ContractZoneAssignmentModal } from './components/ContractZoneAssignmentModal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { useToast } from '@/components/ui/Toast';
import { getContractStatus, getPackageType } from '@/utils/status';
import { formatCurrency, formatDate, getDaysRemaining } from '@/utils/format';


export default function ContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { toast } = useToast();
  const companyId = profile?.company_id || '';

  const { data: contract, isLoading, error } = useMaintenanceContractDetail(id);
  const { deleteContract, isDeleting } = useMaintenanceContracts(companyId);
  const { data: scopes = [] } = useContractScopes(id);
  const { data: sla = [] } = useContractSLA(id);
  const { data: contractZones = [], isLoading: isLoadingZones } = useContractZones(id);

  const [activeTab, setActiveTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showAssignZone, setShowAssignZone] = useState(false);

  const { mutateAsync: removeZone } = useRemoveContractZone();

  const handleDeleteConfirm = async () => {
    if (!contract) return;
    try {
      await deleteContract(contract.id);
      toast.success('Đã xóa hợp đồng thành công');
      navigate('/maintenance/contracts');
    } catch (error: any) {
      toast.error('Lỗi khi xóa: ' + error.message);
    }
  };

  const handleRemoveZone = async (assignmentId: string) => {
    if (!window.confirm('Bạn có chắc chắn muốn gỡ khu vực này khỏi hợp đồng?')) return;
    try {
      await removeZone({ assignmentId, contractId: id! });
      toast.success('Đã gỡ khu vực thành công');
    } catch (error: any) {
      toast.error('Lỗi khi gỡ khu vực: ' + error.message);
    }
  };

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải thông tin hợp đồng...</div>;
  if (!contract) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy dữ liệu hợp đồng</div>;

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'scope', label: 'Phạm vi' },
    { value: 'zones', label: 'Khu vực' },
    { value: 'sla', label: 'SLA' },
    { value: 'costs', label: 'Chi phí' },
    { value: 'plans', label: 'Kế hoạch' },
    { value: 'documents', label: 'Tài liệu' },
    { value: 'logs', label: 'Nhật ký' },
  ];

  const status = getContractStatus(contract.status_code);

  return (
    <PageDetailLayout
      title={contract.contract_name}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge variant={status.variant} className="font-bold text-[10px] ">
            <i className={`${status.icon} mr-1`} />
            {status.label}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">{contract.contract_code}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-building-line" />
            {contract.project?.name || 'Chưa gán dự án'}
          </span>
        </div>
      }
      breadcrumbs={[
        {
          label: contract.contract_type === 'construction' ? 'Hợp đồng thi công' : 'Hợp đồng bảo dưỡng',
          path: contract.contract_type === 'construction' ? '/construction/contracts' : '/maintenance/contracts'
        },
        { label: contract.contract_code }
      ]}
      headerActions={
        <div className="flex gap-2">
          <AuthButton module="maintenance_contracts" action="update" variant="outline" icon="ri-edit-line" onClick={() => setShowEdit(true)}>
            Chỉnh sửa
          </AuthButton>
          <AuthButton module="maintenance_contracts" action="delete" variant="outline" icon="ri-delete-bin-line" className="text-red-500 hover:bg-red-50" onClick={() => setShowDelete(true)}>
            Xóa
          </AuthButton>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {activeTab === 'overview' && (
            <>
              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Thông tin hợp đồng
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-y-6 gap-x-4">
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Gói thầu</p>
                    <Badge variant={getPackageType(contract.package_type).variant} className="font-bold text-[10px] ">
                      {getPackageType(contract.package_type).label}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Dự án</p>
                    <p className="text-sm font-semibold text-slate-700">{contract.project?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Giá trị hợp đồng</p>
                    <p className="text-sm font-bold text-slate-900">
                      {formatCurrency(contract.contract_value)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Ngày bắt đầu</p>
                    <p className="text-sm font-semibold text-slate-700">{formatDate(contract.start_date)}</p>
                  </div>
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Ngày kết thúc</p>
                    <p className="text-sm font-semibold text-slate-700">{formatDate(contract.end_date)}</p>
                  </div>
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Thời hạn còn lại</p>
                    <p className="text-sm font-semibold text-os-primary">
                      {getDaysRemaining(contract.end_date)} ngày
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-text-snippet text-os-primary" />
                  Mô tả & Ghi chú
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {contract.description || 'Chưa có mô tả chi tiết cho hợp đồng này.'}
                </p>
              </Card>
            </>
          )}

          {activeTab === 'zones' && (
            <Card className="p-0 overflow-hidden">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                <div>
                  <h3 className="font-bold text-slate-900">Danh sách khu vực áp dụng</h3>
                  <p className="text-xs text-slate-500 mt-1">Các khu vực thuộc dự án được áp dụng gói bảo trì này</p>
                </div>
                <div className="flex gap-2">
                  <Badge variant="custom" className="bg-white">{contractZones.length} Khu vực</Badge>
                  <Button variant="primary" size="sm" icon="ri-add-line" onClick={() => setShowAssignZone(true)}>
                    Gán khu vực
                  </Button>
                </div>
              </div>

              <div className="hidden md:block">
                <ContractZonesTable
                  zones={contractZones}
                  isLoading={isLoadingZones}
                  onDelete={handleRemoveZone}
                />
              </div>
              <div className="md:hidden">
                <ContractZonesMobileList
                  zones={contractZones}
                  isLoading={isLoadingZones}
                  onDelete={handleRemoveZone}
                />
              </div>
            </Card>
          )}

          {activeTab !== 'overview' && activeTab !== 'zones' && (
            <FeaturePlaceholder
              title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
              description="Phần này đang được tích hợp dữ liệu chi tiết từ các bảng liên quan."
            />
          )}
        </div>

        <div className="space-y-6">
          {activeTab === 'overview' || activeTab === 'scope' ? (
            <Card className="p-6">
              <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-survey-line text-slate-400" />
                Tóm tắt phạm vi
              </h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500">Số hạng mục:</span>
                  <span className="font-bold text-slate-700">{scopes.length}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500">Chỉ số SLA:</span>
                  <span className="font-bold text-slate-700">{sla.length}</span>
                </div>
                <div className="pt-4 border-t border-slate-50">
                  <p className="text-[10px]  text-slate-400 font-bold mb-2">Hành động nhanh</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm" icon="ri-add-line" className="text-[10px]" onClick={() => toast.info('Tính năng thêm Scope đang được phát triển')}>Thêm Scope</Button>
                    <Button variant="outline" size="sm" icon="ri-shield-check-line" className="text-[10px]" onClick={() => toast.info('Tính năng gán SLA đang được phát triển')}>Gán SLA</Button>
                  </div>
                </div>
              </div>
            </Card>
          ) : activeTab === 'zones' ? (
            <Card className="p-6">
              <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-map-pin-line text-slate-400" />
                Phạm vi khu vực
              </h4>
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-500 font-medium">Khu vực áp dụng</span>
                    <span className="text-sm font-black text-slate-900">{contractZones.length}</span>
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-50">
                  <p className="text-[10px]  text-slate-400 font-bold mb-2">Hành động nhanh</p>
                  <Button variant="outline" size="sm" icon="ri-map-pin-add-line" className="text-xs w-full" onClick={() => setShowAssignZone(true)}>
                    Gán khu vực mới
                  </Button>
                </div>
              </div>
            </Card>
          ) : activeTab === 'sla' ? (
            <Card className="p-6">
              <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-shield-star-line text-slate-400" />
                Cam kết chất lượng
              </h4>
              <div className="space-y-4">
                <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-emerald-700 font-medium">SLA Mục tiêu</span>
                    <span className="text-sm font-black text-emerald-700">95%</span>
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-50">
                  <p className="text-[10px]  text-slate-400 font-bold mb-2">Hành động nhanh</p>
                  <Button variant="outline" size="sm" icon="ri-settings-3-line" className="text-xs w-full" onClick={() => toast.info('Tính năng thiết lập SLA đang được phát triển')}>
                    Cấu hình chỉ số
                  </Button>
                </div>
              </div>
            </Card>
          ) : activeTab === 'costs' ? (
            <Card className="p-6">
              <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-money-dollar-circle-line text-slate-400" />
                Tình hình chi phí
              </h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500">Đã thanh toán:</span>
                  <span className="font-bold text-emerald-600">0%</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: '0%' }} />
                </div>
                <div className="pt-4 border-t border-slate-50">
                  <p className="text-[10px]  text-slate-400 font-bold mb-2">Hành động nhanh</p>
                  <Button variant="outline" size="sm" icon="ri-add-circle-line" className="text-xs w-full" onClick={() => toast.info('Tính năng thêm chi phí đang được phát triển')}>
                    Ghi nhận chi phí
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <Card className="p-6">
              <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-history-line text-slate-400" />
                Tiến độ tổng quát
              </h4>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-slate-500 font-medium">Hoàn thành thực tế</span>
                    <span className="text-xs font-bold text-emerald-600">0%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: '0%' }} />
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-50">
                  <p className="text-[10px]  text-slate-400 font-bold mb-2">Hành động nhanh</p>
                  <Button variant="outline" size="sm" icon="ri-refresh-line" className="text-xs w-full" onClick={() => toast.info('Đang làm mới dữ liệu...')}>
                    Cập nhật tiến độ
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      <ContractFormModal
        isOpen={showEdit}
        contract={contract}
        companyId={companyId}
        defaultType={contract.contract_type}
        onClose={() => setShowEdit(false)}
      />

      <ConfirmDialog
        isOpen={showDelete}
        title="Xác nhận xóa hợp đồng"
        message={`Bạn có chắc chắn muốn xóa hợp đồng "${contract.contract_name}"?`}
        confirmText="Xác nhận xóa"
        isDestructive={true}
        isLoading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onClose={() => setShowDelete(false)}
      />

      <ContractZoneAssignmentModal
        isOpen={showAssignZone}
        onClose={() => setShowAssignZone(false)}
        contractId={id!}
        projectId={contract.project_id}
        companyId={companyId}
        alreadyAssignedZoneIds={contractZones.map((cz: any) => cz.zone_id)}
      />
    </PageDetailLayout>
  );
}
